/*
  Promo band (JX-157): keeps sections/promo-band.liquid in step with the cart
  and plays the transitions.

  The section renders the right state on page load. This listens for the
  theme's own events, cart:change (carries the new cart) and cart:refresh
  (asks us to fetch it), then updates the slots, thumbnails, copy and progress
  in place. It counts shipping units only, the same rule as the Liquid and the
  cart drawer's pair ladder, so the three never disagree.

  Moving from one state to a better one adds a class for the CSS to animate:
  a pop on the newly filled slot, and on reaching the free pair, a confetti
  burst from the free slot. All of it is skipped under prefers-reduced-motion.
*/
(() => {
  if (window.customElements.get('promo-band')) return;

  const RANK = { empty: 0, started: 1, 'next-free': 2, unlocked: 3 };
  const camel = (state) => state.replace(/-(\w)/g, (m, c) => c.toUpperCase()).replace(/^\w/, (c) => c.toUpperCase());
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class PromoBand extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;

      document.addEventListener('cart:change', (event) => {
        const cart = event.detail && event.detail.cart;
        if (cart) this.update(cart);
      }, { signal });

      document.addEventListener('cart:refresh', () => this.refresh(), { signal });
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
    }

    async refresh() {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        if (response.ok) this.update(await response.json());
      } catch (error) {
        /* The band keeps its last state; the server render on the next page load is authoritative. */
      }
    }

    units(cart) {
      // Newest line first, as Shopify lists it; one entry per unit.
      const list = [];
      (cart.items || []).forEach((item) => {
        if (item.requires_shipping === false) return;
        for (let i = 0; i < item.quantity; i++) list.push(item.image || '');
      });
      return list;
    }

    sized(url) {
      if (!url) return '';
      return url + (url.includes('?') ? '&' : '?') + 'width=96';
    }

    update(cart) {
      const buy = Number(this.dataset.buy) || 2;
      const group = Number(this.dataset.group) || 3;
      const units = this.units(cart);
      const count = units.length;
      const inGroup = count % group;

      let state;
      let filled;
      if (count === 0) {
        state = 'empty';
        filled = 0;
      } else if (inGroup === 0) {
        state = 'unlocked';
        filled = group;
      } else if (inGroup >= buy) {
        state = 'next-free';
        filled = inGroup;
      } else {
        state = 'started';
        filled = inGroup;
      }

      const previousState = this.dataset.state;
      const previousFilled = this.querySelectorAll('.promo-band__slot.is-filled').length;
      if (state === previousState && filled === previousFilled) return;

      this.dataset.state = state;

      this.querySelectorAll('.promo-band__slot').forEach((slot, index) => {
        const isFilled = index < filled;
        slot.classList.toggle('is-filled', isFilled);
        slot.classList.toggle('is-new', isFilled && index >= previousFilled && RANK[state] >= RANK[previousState]);
        slot.classList.toggle('is-next', state === 'started' && index === filled && index < buy);

        const tile = slot.querySelector('.promo-band__tile');
        let img = tile.querySelector('img');
        const src = isFilled ? this.sized(units[filled - 1 - index]) : '';
        if (src) {
          if (!img) {
            img = document.createElement('img');
            img.alt = '';
            img.width = 48;
            img.height = 36;
            tile.prepend(img);
          }
          img.src = src;
        } else if (img) {
          img.remove();
        }
      });

      const doneInGroup = String(inGroup);
      const paidRemaining = String(Math.max(buy - inGroup, 0));
      const fill = (text) => (text || '').replace('[done]', doneInGroup).replace('[remaining]', paidRemaining);
      this.querySelector('.promo-band__headline').textContent = fill(this.dataset[`headline${camel(state)}`]);
      this.querySelector('.promo-band__status').textContent = fill(this.dataset[`status${camel(state)}`]);

      const bar = this.querySelector('.promo-band__progress > span');
      if (bar) bar.style.setProperty('--promo-band-progress', `${Math.round((filled / group) * 100)}%`);

      if (RANK[state] > RANK[previousState] || (state === previousState && filled > previousFilled)) {
        this.celebrate(state === 'next-free' || state === 'unlocked');
      }
    }

    celebrate(big) {
      if (reducedMotion()) return;

      this.classList.remove('is-celebrating');
      void this.offsetWidth; // restart the animation
      this.classList.add('is-celebrating');
      window.clearTimeout(this.celebrationTimer);
      this.celebrationTimer = window.setTimeout(() => {
        this.classList.remove('is-celebrating');
        this.querySelectorAll('.is-new').forEach((slot) => slot.classList.remove('is-new'));
      }, 1600);

      if (!big) return;

      const origin = this.querySelector('.promo-band__slot--free .promo-band__tile');
      if (!origin) return;

      const burst = document.createElement('span');
      burst.className = 'promo-band__confetti';
      burst.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < 18; i++) {
        const piece = document.createElement('i');
        const angle = (Math.PI * 2 * i) / 18 + Math.random() * 0.4;
        const distance = 28 + Math.random() * 34;
        piece.style.setProperty('--x', `${Math.cos(angle) * distance}px`);
        piece.style.setProperty('--y', `${Math.sin(angle) * distance}px`);
        piece.style.setProperty('--r', `${Math.round(Math.random() * 360)}deg`);
        piece.style.setProperty('--d', `${Math.round(Math.random() * 120)}ms`);
        burst.append(piece);
      }
      origin.append(burst);
      window.setTimeout(() => burst.remove(), 1400);
    }
  }

  window.customElements.define('promo-band', PromoBand);
})();
